import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Collapse, Grid, IconButton, Link,
  MenuItem, Paper, TextField, Tooltip, Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { frFR } from '@mui/x-data-grid/locales';
import {
  Download as DownloadIcon, FilterAltOffOutlined as FilterAltOffIcon,
  FilterAltOutlined as FilterAltIcon, InfoOutlined as InfoIcon,
  RestartAlt as RestartAltIcon, Search as SearchIcon,
} from '@mui/icons-material';
import PartnerMainContainer from '../../../components/PartnerMainContainer';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import TitlePartner from '../component/Title';
import PartnerEvalution from '../Parterns/PartnerEvalution';
import MultiPartnerDetailsDialog from './MultiPartnerDetailsDialog';
import ExportContainer from '../Export/ExportContainer';
import MultiPartnerService from '../../../services/MultiPartnerService';
import { exporterTableauPartenaires } from '../Export/exporterTableauPartenaires';
import { formatDate, formatPartnerSource, isValidValue, normalizeText } from '../../../helpers/PartnerHelper';
import usePartnerFilterConsultation from '../hooks/usePartnerFilterConsultation';
import {
  partnerDataGridSx, partnerFilterButtonSx, partnerFilterFieldSx,
  partnerTableColors,
} from '../Parterns/partnerListTheme';

const normalizeConflict = (conflict) => ({
  ...conflict,
  id: conflict?.key || `conflict-${conflict?.personnelId || conflict?.peid}`,
  partenaires: Array.isArray(conflict?.partenaires) ? conflict.partenaires : [],
  dirs: Array.isArray(conflict?.dirs) ? conflict.dirs : [],
  entries: Array.isArray(conflict?.entries) ? conflict.entries : [],
  source_db: conflict?.source_db || 'bdd partenaire',
});


const MultiPartner = () => {
  const [conflicts, setConflicts] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [detailError, setDetailError] = useState('');
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedConflict, setSelectedConflict] = useState(null);
  const detailRequestRef = useRef(0);
  const [selectedPartnerName, setSelectedPartnerName] = useState('');
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);
  const [selectedPartnerPid, setSelectedPartnerPid] = useState('');
  const [selectedPartnerSourceDb, setSelectedPartnerSourceDb] = useState('');
  const [isPartnerDetailOpen, setIsPartnerDetailOpen] = useState(false);

  const defaultFilters = useMemo(() => ({ type: '', direction: '', sortBy: 'nom', search: '' }), []);
  const [filters, setFilters] = useState(defaultFilters);

  usePartnerFilterConsultation(filters, (nextFilters) => MultiPartnerService.getAllRows(nextFilters));

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    MultiPartnerService.getAllRows().then((result) => {
      if (!active) return;
      const data = Array.isArray(result?.data) ? result.data.map(normalizeConflict) : [];
      const message = typeof result?.message === 'string' ? result.message.trim() : '';
      setConflicts(data);
      setLoadError(result?.status === 0 && !data.length && !/^aucun/i.test(message) ? message : '');
    }).catch(() => {
      if (active) setLoadError('Impossible de charger les conflits multi-partenaires.');
    }).finally(() => {
      if (active) setIsLoading(false);
    });
    return () => { active = false; };
  }, []);

  const directionOptions = useMemo(() => [...new Set(conflicts.flatMap((row) => row.dirs))]
    .filter(isValidValue).sort((a, b) => a.localeCompare(b, 'fr')), [conflicts]);

  const rows = useMemo(() => conflicts.filter((conflict) => {
    if (filters.type === 'chevauchement' && !conflict.chevauchement) return false;
    if (filters.type === 'ci_conflit' && !conflict.ciConflit) return false;
    if (filters.direction && !conflict.dirs.includes(filters.direction)) return false;
    const search = normalizeText(filters.search);
    if (search && !normalizeText(`${conflict.nom} ${conflict.cin}`).includes(search)) return false;
    return true;
  }).sort((left, right) => filters.sortBy === 'nbPartenaires'
    ? right.nbPartenaires - left.nbPartenaires
    : left.nom.localeCompare(right.nom, 'fr')), [conflicts, filters]);

  useEffect(() => setPage(0), [filters]);

  const chevauchementsCount = conflicts.filter((row) => row.chevauchement).length;
  const ciConflitsCount = conflicts.filter((row) => row.ciConflit).length;
  const activeFiltersCount = ['type', 'direction', 'search'].filter((field) => Boolean(filters[field])).length;

  const openPartnerDetail = (partner) => {
    const name = typeof partner === 'string' ? partner : partner?.partenaire;
    if (!isValidValue(name)) return;
    const id = typeof partner === 'object' ? Number(partner.partenaireId) || null : null;
    setSelectedPartnerName(name);
    setSelectedPartnerId(id);
    setSelectedPartnerPid(id ? `P-${String(id).padStart(5, '0')}` : '');
    setSelectedPartnerSourceDb(typeof partner === 'object' ? partner.source_db || '' : '');
    setIsPartnerDetailOpen(true);
  };

  const loadConflictDetail = async (conflict) => {
    if (!conflict?.key) return;
    const requestId = detailRequestRef.current + 1;
    detailRequestRef.current = requestId;
    setSelectedConflict({ ...normalizeConflict(conflict), entries: [] });
    setDetailError('');
    setIsDetailLoading(true);
    const result = await MultiPartnerService.getConflictDetail(conflict.key, conflict.personnelId);
    if (detailRequestRef.current !== requestId) return;
    if (result?.status === 1 && result?.data) {
      setSelectedConflict(normalizeConflict({ ...conflict, ...result.data }));
    } else {
      setDetailError(result?.message || 'Impossible de charger le détail du conflit.');
    }
    setIsDetailLoading(false);
  };

  const closeConflictDetail = () => {
    detailRequestRef.current += 1;
    setSelectedConflict(null);
    setDetailError('');
    setIsDetailLoading(false);
  };

  const columns = [
    { field: 'nom', headerName: 'Personnel', minWidth: 190, flex: 1, renderCell: ({ row, value }) => <Box><Typography sx={{ fontSize: 12, fontWeight: 700 }}>{value}</Typography><Typography sx={{ color: '#667085', fontSize: 10 }}>{row.peid || '—'}</Typography></Box> },
    { field: 'cin', headerName: 'CIN', minWidth: 125, flex: 0.65, renderCell: ({ value }) => value || '—' },
    { field: 'partenaires', headerName: 'Partenaires impliqués', minWidth: 250, flex: 1.4, sortable: false, renderCell: ({ row }) => <Box sx={{ display: 'flex', gap: 0.5, overflow: 'hidden' }}>{row.partenaires.map((partner) => <Link component="button" key={partner} type="button" underline="hover" onClick={(event) => { event.stopPropagation(); openPartnerDetail(partner); }} onDoubleClick={(event) => event.stopPropagation()} sx={{ color: partnerTableColors.dark, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{partner}</Link>)}</Box> },
    { field: 'nbEntrees', headerName: 'Entrées', width: 80, align: 'center', headerAlign: 'center' },
    { field: 'type', headerName: 'Type de conflit', minWidth: 210, flex: 1.1, sortable: false, renderCell: ({ row }) => <Box sx={{ display: 'flex', gap: 0.5 }}>{row.chevauchement && <Chip size="small" label="Chevauchement" sx={{ bgcolor: '#fee4e2', color: '#b42318', fontSize: 10, fontWeight: 700 }} />}{row.ciConflit && <Chip size="small" label="CI + Personnel" sx={{ bgcolor: '#f0e7ff', color: '#6941c6', fontSize: 10, fontWeight: 700 }} />}</Box> },
    { field: 'dirs', headerName: 'Directions', minWidth: 130, flex: 0.75, renderCell: ({ value }) => value.join(', ') || '—' },
    { field: 'source_db', headerName: 'Base source', minWidth: 175, flex: 0.9, renderCell: ({ value }) => formatPartnerSource(value) },
    { field: 'details', headerName: 'Détails', width: 80, sortable: false, filterable: false, align: 'center', headerAlign: 'center', renderCell: ({ row }) => <Tooltip title="Afficher le conflit"><IconButton size="small" onClick={(event) => { event.stopPropagation(); loadConflictDetail(row); }} onDoubleClick={(event) => event.stopPropagation()}><InfoIcon fontSize="small" /></IconButton></Tooltip> },
  ];

  const exportRows = rows.map((row) => ({
    'ID Personnel': row.peid || '', Nom: row.nom || '', CIN: row.cin || '',
    Partenaires: row.partenaires.join(' | '), 'Nb entrées': row.nbEntrees || 0,
    Chevauchement: row.chevauchement ? 'Oui' : 'Non',
    'CI + Personnel': row.ciConflit ? 'Oui' : 'Non', Directions: row.dirs.join(' | '),
    'Base source': formatPartnerSource(row.source_db),
  }));
  const fileName = `multi_partenaires_${new Date().toISOString().split('T')[0]}`;
  const exporter = (format, details = []) => exporterTableauPartenaires({
    format, lignes: exportRows, entetes: Object.keys(exportRows[0] || { Conflit: '' }),
    titre: 'Rapport multi-partenaires', nomFichier: fileName, nomFeuille: 'Conflits',
    resume: `${rows.length} conflit(s)`,
    rapportExcel: format === 'xlsx' ? ({ colonnes, valeurs }) => [
      ['RAPPORT MULTI-PARTENAIRES'], ["Date d'exportation", new Date().toLocaleString('fr-FR')],
      ['Nombre de conflits', rows.length],
      ['Chevauchements', rows.filter((row) => row.chevauchement).length],
      ['CI + Personnel', rows.filter((row) => row.ciConflit).length],
      ['Entrées détaillées', details.length], [], colonnes, ...valeurs,
    ] : null,
    feuillesSupplementaires: format === 'xlsx' ? [{ nom: 'Details', lignes: details,
      entetes: ['ID Personnel', 'Nom conflit', 'CIN', 'Partenaire', 'Role', 'Fonction',
        'Direction', 'Debut', 'Fin', 'Chevauchement', 'CI + Personnel'] }] : [],
    audit: { menu: 'multi_partner', format, filters, rowCount: rows.length },
  });
  const handleExcelExport = async () => {
    setIsExporting(true);
    try {
      const details = await Promise.all(rows.map(async (row) => {
        const resultat = await MultiPartnerService.getConflictDetail(row.key, row.personnelId);
        if (resultat?.status !== 1 && resultat?.status !== true) {
          throw new Error('Impossible de charger les détails des conflits pour le rapport Excel.');
        }
        return (resultat.data?.entries || []).map((entree) => ({
          'ID Personnel': row.peid || '', 'Nom conflit': row.nom || '', CIN: row.cin || '',
          Partenaire: entree.partenaire || '', Role: entree.role || '',
          Fonction: entree.fonction || '', Direction: entree.direction || '',
          Debut: formatDate(entree.dateDebut), Fin: formatDate(entree.dateFin),
          Chevauchement: row.chevauchement ? 'Oui' : 'Non',
          'CI + Personnel': row.ciConflit ? 'Oui' : 'Non',
        }));
      }));
      exporter('xlsx', details.flat());
    } catch (erreur) {
      console.error('Export Excel multi-partenaires impossible :', erreur);
    } finally {
      setIsExporting(false);
    }
  };
  const handleCsvExport = () => exporter('csv');
  const handlePdfExport = () => exporter('pdf');
  const handlePrint = () => exporter('print');

  return <PartnerMainContainer>
    <LoadingSimpleBackdrop openLoadingSimpleBackdrop={isLoading || isExporting} />
    <TitlePartner title="Multi-partenaires" sub={`(${rows.length} conflits) - chevauchements & CI dans le personnel`} icon="⚠️" />
    {loadError && <Alert severity="error" sx={{ mb: 1.5 }}>{loadError}</Alert>}
    {!!conflicts.length && <Alert severity="warning" sx={{ mb: 1.5 }}>{conflicts.length} conflit(s) détecté(s) · Chevauchements : {chevauchementsCount} · CI/Personnel : {ciConflitsCount}</Alert>}

    <Paper elevation={0} sx={{ border: '1px solid #cbd8e6', borderRadius: '8px', mb: 2, p: 1.25 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, justifyContent: 'space-between', mb: filtersVisible ? 1.5 : 0 }}>
        <Box><Typography color="#52677c" fontSize={10} fontWeight={700} letterSpacing="0.05em">FILTRES</Typography><Typography color="#667085" fontSize={10}>{activeFiltersCount} critère(s) actif(s)</Typography></Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          {filtersVisible && <Button disabled={!activeFiltersCount} onClick={() => setFilters(defaultFilters)} size="small" startIcon={<RestartAltIcon />} sx={{ ...partnerFilterButtonSx, borderColor: '#9fb1c3', color: partnerTableColors.dark }} variant="outlined">Réinitialiser</Button>}
          <Button onClick={() => setFiltersVisible((value) => !value)} size="small" startIcon={filtersVisible ? <FilterAltOffIcon /> : <FilterAltIcon />} sx={{ ...partnerFilterButtonSx, borderColor: '#9fb1c3', color: partnerTableColors.dark }} variant="outlined">{filtersVisible ? 'Masquer filtres' : 'Afficher filtres'}</Button>
          <Button disabled={!rows.length} onClick={(event) => setExportAnchorEl(event.currentTarget)} size="small" startIcon={<DownloadIcon />} sx={{ ...partnerFilterButtonSx, bgcolor: partnerTableColors.gold, color: partnerTableColors.text, fontWeight: 800, '&:hover': { bgcolor: '#c39b2e' } }} variant="contained">Exporter</Button>
        </Box>
      </Box>
      <Collapse in={filtersVisible} timeout="auto" unmountOnExit><Grid container spacing={1.25}>
        <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" placeholder="Nom ou CIN..." value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} sx={partnerFilterFieldSx} InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: '#667085' }} /> }} /></Grid>
        <Grid item xs={12} sm={6} md={3}><TextField select fullWidth label="Type de conflit" size="small" value={filters.type} onChange={(event) => setFilters((current) => ({ ...current, type: event.target.value }))} sx={partnerFilterFieldSx}><MenuItem value="">Tous</MenuItem><MenuItem value="chevauchement">Chevauchement de dates</MenuItem><MenuItem value="ci_conflit">CI + Personnel</MenuItem></TextField></Grid>
        <Grid item xs={12} sm={6} md={3}><TextField select fullWidth label="Direction" size="small" value={filters.direction} onChange={(event) => setFilters((current) => ({ ...current, direction: event.target.value }))} sx={partnerFilterFieldSx}><MenuItem value="">Toutes</MenuItem>{directionOptions.map((direction) => <MenuItem key={direction} value={direction}>{direction}</MenuItem>)}</TextField></Grid>
        <Grid item xs={12} sm={6} md={3}><TextField select fullWidth label="Trier par" size="small" value={filters.sortBy} onChange={(event) => setFilters((current) => ({ ...current, sortBy: event.target.value }))} sx={partnerFilterFieldSx}><MenuItem value="nom">Nom</MenuItem><MenuItem value="nbPartenaires">Nombre de partenaires</MenuItem></TextField></Grid>
      </Grid></Collapse>
    </Paper>

    <Paper elevation={0} sx={{ border: '1px solid #cbd8e6', borderRadius: '8px', overflow: 'hidden' }}>
      <Box sx={{ alignItems: 'center', bgcolor: '#f8fbff', borderBottom: `1px solid ${partnerTableColors.border}`, display: 'flex', justifyContent: 'space-between', px: 1.75, py: 1.5 }}><Typography sx={{ color: '#1f2a44', fontSize: 13, fontWeight: 600 }}>Liste des conflits multi-partenaires — 1 ligne = 1 personnel</Typography><Typography sx={{ color: '#4b6488', fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{rows.length} conflit(s)</Typography></Box>
      <Box sx={{ height: 560 }}><DataGrid rows={rows} columns={columns} columnHeaderHeight={48} disableRowSelectionOnClick localeText={frFR.components.MuiDataGrid.defaultProps.localeText} onPaginationModelChange={(model) => { setPage(model.page); setPageSize(model.pageSize); }} onRowDoubleClick={({ row }) => loadConflictDetail(row)} pageSizeOptions={[10, 15, 30, 50]} pagination paginationModel={{ page, pageSize }} rowHeight={48} sx={partnerDataGridSx} /></Box>
    </Paper>

    <MultiPartnerDetailsDialog conflict={selectedConflict} loading={isDetailLoading} error={detailError} onClose={closeConflictDetail} onPartnerOpen={openPartnerDetail} />
    <PartnerEvalution open={isPartnerDetailOpen} onClose={() => setIsPartnerDetailOpen(false)} partnerId={selectedPartnerId} partnerPid={selectedPartnerPid} partnerName={selectedPartnerName} sourceDb={selectedPartnerSourceDb} />
    <ExportContainer exportAnchorEl={exportAnchorEl} handleExportClose={() => setExportAnchorEl(null)} handlePrint={handlePrint} handleExcelExport={handleExcelExport} handleCsvExport={handleCsvExport} handlePdfExport={handlePdfExport} />
  </PartnerMainContainer>;
};

export default MultiPartner;
